import ConsentBanner from "./components/ConsentBanner";
import { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider, useTheme } from "@/contexts/ThemeContext";
import { Toaster, toast } from "sonner";
import MobileInputScrollWatcher from "@/components/MobileInputScrollWatcher";
// Every page is a separate chunk, fetched on navigation instead of all
// up front — the app has 38 pages (17k lines) and none of that needs to
// be in the first paint's bundle.
const Landing = lazy(() => import("@/pages/Landing"));
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const DashboardLayout = lazy(() => import("@/pages/DashboardLayout"));
const Marketplace = lazy(() => import("@/pages/Marketplace"));
const FreelancerDetail = lazy(() => import("@/pages/FreelancerDetail"));
const Missions = lazy(() => import("@/pages/Missions"));
const MissionCreate = lazy(() => import("@/pages/MissionCreate"));
const AiMissionCreate = lazy(() => import("@/pages/AiMissionCreate"));
const AdminNotifications = lazy(() => import("@/pages/AdminNotifications"));
const SignPublicPage = lazy(() => import("@/pages/SignPublicPage"));
const FreelanceMatching = lazy(() => import("@/pages/FreelanceMatching"));
const FreelanceMyMissions = lazy(() => import("@/pages/FreelanceMyMissions"));
const FreelanceAdministratif = lazy(() => import("@/pages/FreelanceAdministratif"));
const FreelanceCra = lazy(() => import("@/pages/FreelanceCra"));
const FreelanceBilling = lazy(() => import("@/pages/FreelanceBilling"));
const ClientCraApproval = lazy(() => import("@/pages/ClientCraApproval"));
const ClientAdministratif = lazy(() => import("@/pages/ClientAdministratif"));
const ClientQuotes = lazy(() => import("@/pages/ClientQuotes"));
const Invoices = lazy(() => import("@/pages/Invoices"));
const Payables = lazy(() => import("@/pages/Payables"));
const AdminAuditLog = lazy(() => import("@/pages/AdminAuditLog"));
const AdminCompliance = lazy(() => import("@/pages/AdminCompliance"));
const AdminLeadsEconomicProfile = lazy(() => import("@/pages/AdminLeadsEconomicProfile"));
const AdminTjmReferences = lazy(() => import("@/pages/AdminTjmReferences"));
const AdminSuggestions = lazy(() => import("@/pages/AdminSuggestions"));
const AdminPortage = lazy(() => import("@/pages/AdminPortage"));
const RoadmapVote = lazy(() => import("@/pages/RoadmapVote"));
const AdminExpertiseQueue = lazy(() => import("@/pages/AdminExpertiseQueue"));
const Profile = lazy(() => import("@/pages/Profile"));
const Earnings = lazy(() => import("@/pages/Earnings"));
const FreelanceReferrals = lazy(() => import("@/pages/FreelanceReferrals"));
const AdminReferrals = lazy(() => import("@/pages/AdminReferrals"));
const ProposalPublic = lazy(() => import("@/pages/ProposalPublic"));
const AppointmentPublic = lazy(() => import("@/pages/AppointmentPublic"));
const MissionDetail = lazy(() => import("@/pages/MissionDetail"));
const DashboardHome = lazy(() => import("@/pages/DashboardHome"));
const InvoicePayPage = lazy(() => import("@/pages/InvoicePayPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentSuccessPage"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const PapyrusList = lazy(() => import("@/pages/PapyrusList"));
const PapyrusArticle = lazy(() => import("@/pages/PapyrusArticle"));
const Subscribe = lazy(() => import("@/pages/Subscribe"));
const AuthRolePicker = lazy(() => import("@/pages/AuthRolePicker"));
const NotFound = lazy(() => import("@/pages/NotFound"));
import Skeleton from "@/components/ui/Skeleton";
import PageTransition from "@/components/layout/PageTransition";

/** Redirection interne qui conserve `?status=`, `?months=` et `?client=`. */
function RedirectWithQuery({ to }) {
  const { search } = useLocation();
  return <Navigate to={{ pathname: to, search }} replace />;
}

import SpartanLoader from "@/components/ui/SpartanLoader";

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <SpartanLoader />
    </div>
  );
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted">Chargement…</div>;
  if (!user) return <Navigate to="/login" replace />;
  // roles: optional whitelist (e.g. ["admin"]) — pages omitting it stay open to any authenticated user.
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return children;
}

// Retour du callback SSO : le backend redirige vers /app (ou /auth/role) en
// ajoutant `?sso=connected`. On l'annonce une fois, puis on retire le paramètre
// pour qu'un rafraîchissement ne rejoue pas le message.
function SsoConfirmation() {
  const location = useLocation();
  const navigate = useNavigate();
  const announced = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("sso") !== "connected" || announced.current) return;
    announced.current = true;
    toast.success("Connexion Google réussie");
    params.delete("sso");
    const search = params.toString();
    navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  return null;
}

function GatedRoutes() {
  const location = useLocation();
  // La connexion Google passe désormais par `GET /api/auth/sso/start/google`
  // (lot 3) : plus aucun fragment `#session_id=…` n'arrive dans l'URL, donc
  // plus rien à intercepter ici avant les routes protégées.
  return (
    <Suspense fallback={<RouteFallback />}>
      <PageTransition location={location}>
        <Routes location={location}>
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/sign/:token" element={<SignPublicPage />} />
        <Route path="/proposal/:slug" element={<ProposalPublic />} />
        {/* Page de suivi d'un rendez-vous : le lien des e-mails de confirmation
            et de rappel. Publique par construction — c'est le jeton de l'URL qui
            authentifie (aucune session requise). */}
        <Route path="/rendez-vous/:appointmentId/:token" element={<AppointmentPublic />} />
        <Route path="/papyrus" element={<PapyrusList />} />
        <Route path="/papyrus/:slug" element={<PapyrusArticle />} />
<Route path="/subscribe" element={<Subscribe />} />
        <Route path="/auth/role" element={<AuthRolePicker />} />
        <Route path="/app" element={<Protected><DashboardLayout /></Protected>}>
          <Route index element={<DashboardHome />} />
          <Route path="marketplace" element={<Marketplace />} />
          <Route path="freelancer/:id" element={<FreelancerDetail />} />
          <Route path="missions" element={<Missions />} />
          <Route path="missions/new" element={<MissionCreate />} />
          <Route path="missions/ai" element={<AiMissionCreate />} />
          <Route path="notifications" element={<AdminNotifications />} />
          <Route path="matching" element={<FreelanceMatching />} />
          <Route path="freelance/missions" element={<FreelanceMyMissions />} />
          <Route path="administratif" element={<FreelanceAdministratif />}>
            <Route index element={<Navigate to="cra" replace />} />
            <Route path="cra" element={<FreelanceCra />} />
            <Route path="factures" element={<FreelanceBilling />} />
            <Route path="revenus" element={<Earnings />} />
          </Route>
          <Route path="freelance/cra" element={<Navigate to="/app/administratif/cra" replace />} />
          <Route path="freelance/billing" element={<Navigate to="/app/administratif/factures" replace />} />
          {/* L'administratif client vit sous une seule entrée de menu
              (« Mon administratif »), avec ses trois documents en sous-menus.
              Les anciennes URL restent valides pour les liens déjà envoyés. */}
          <Route path="administratif/client" element={<Protected roles={["client"]}><ClientAdministratif /></Protected>}>
            <Route index element={<Navigate to="cra" replace />} />
            <Route path="cra" element={<ClientCraApproval />} />
            <Route path="devis" element={<ClientQuotes />} />
            <Route path="factures" element={<Invoices scope="client" />} />
          </Route>
          <Route path="client/cra" element={<Navigate to="/app/administratif/client/cra" replace />} />
          <Route path="client/factures" element={<Navigate to="/app/administratif/client/factures" replace />} />
          <Route path="admin/cra" element={<Protected roles={["admin"]}><ClientCraApproval /></Protected>} />
          <Route path="admin/encaissements" element={<Protected roles={["admin"]}><Invoices scope="admin" /></Protected>} />
          <Route path="admin/decaissements" element={<Protected roles={["admin"]}><Payables /></Protected>} />
          {/* Ancienne URL : les liens déjà envoyés (dashboard, favoris,
              e-mails) doivent continuer de fonctionner, query string
              comprise — c'est elle qui porte les filtres du drill-down. */}
          <Route path="admin/factures" element={<Protected roles={["admin"]}><RedirectWithQuery to="/app/admin/encaissements" /></Protected>} />
          <Route path="admin/audit" element={<Protected roles={["admin"]}><AdminAuditLog /></Protected>} />
          <Route path="admin/compliance" element={<Protected roles={["admin"]}><AdminCompliance /></Protected>} />
          <Route path="admin/leads" element={<Protected roles={["admin"]}><AdminLeadsEconomicProfile /></Protected>} />
          <Route path="admin/referrals" element={<Protected roles={["admin"]}><AdminReferrals /></Protected>} />
          <Route path="admin/tjm" element={<Protected roles={["admin"]}><AdminTjmReferences /></Protected>} />
          <Route path="admin/suggestions" element={<Protected roles={["admin"]}><AdminSuggestions /></Protected>} />
          <Route path="admin/portage" element={<Protected roles={["admin"]}><AdminPortage /></Protected>} />
          <Route path="freelance/roadmap" element={<RoadmapVote />} />
          <Route path="admin/roadmap" element={<Protected roles={["admin"]}><RoadmapVote /></Protected>} />
          <Route path="admin/expertise-queue" element={<Protected roles={["admin"]}><AdminExpertiseQueue /></Protected>} />
          <Route path="profile" element={<Profile />} />
          <Route path="earnings" element={<Navigate to="/app/administratif/revenus" replace />} />
          <Route path="freelance/referrals" element={<Protected roles={["freelance"]}><FreelanceReferrals /></Protected>} />
          <Route path="missions/:id" element={<MissionDetail />} />
          <Route path="billing/:invoiceId/pay" element={<InvoicePayPage />} />
          <Route path="payments/success" element={<PaymentSuccessPage />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
      </PageTransition>
    </Suspense>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <ThemedApp />
    </ThemeProvider>
  );
}

function ThemedApp() {
  const { theme } = useTheme();
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" theme={theme === "dark" ? "dark" : "light"} />
        <MobileInputScrollWatcher />
        <GatedRoutes />
        <SsoConfirmation />
        <ConsentBanner />
      </BrowserRouter>
    </AuthProvider>
  );
}
